import Head from 'next/head';

import clsxm from '@/lib/clsxm';

import { SectionHeading, tone } from '@/components/agents/primitives';

import { SHARED_FAQS } from './content';
import { useInteriorLandingT, usePageVars } from './kit';
import type { InteriorLandingPage, LandingFaq } from './types';

/**
 * The page's questions, a room's own (English pages only: they aren't
 * translated), then the shared ones. Also FAQPage structured data.
 */
export default function InteriorFaq({
  page,
  roomFaqs = [],
  sharedFaqs = true,
}: {
  page: InteriorLandingPage;
  roomFaqs?: LandingFaq[];
  /** The questions about the designer every tool page shares. */
  sharedFaqs?: boolean;
}) {
  const { t, lang } = useInteriorLandingT();
  const vars = usePageVars(page);

  const translated = [...page.faqs, ...(sharedFaqs ? SHARED_FAQS : [])].map(
    (faq) => ({
      question: t(faq.question, vars),
      answer: t(faq.answer, vars),
    }),
  );
  const faqs = lang === 'en' ? [...roomFaqs, ...translated] : translated;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };

  return (
    <section id='faq' className='py-20 sm:py-28'>
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
          }}
        />
      </Head>
      <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8'>
        <div className='lg:col-span-4'>
          <SectionHeading size='md' title={t('Questions and answers.')} />
        </div>

        <div className='lg:col-span-8'>
          <ul className='divide-y divide-[#0f0f28]/[0.08] border-y border-[#0f0f28]/[0.08] dark:divide-white/[0.08] dark:border-white/[0.08]'>
            {faqs.map(({ question, answer }, index) => (
              <li key={question}>
                <details className='group' open={index === 0}>
                  <summary
                    className={clsxm(
                      'flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-start transition-colors hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600 dark:hover:text-primary-300 [&::-webkit-details-marker]:hidden',
                      tone.ink,
                    )}
                  >
                    <h3 className='text-lg font-semibold'>{question}</h3>
                    <span
                      aria-hidden='true'
                      className={clsxm(
                        'relative flex h-8 w-8 flex-none items-center justify-center rounded-full',
                        tone.line,
                      )}
                    >
                      <span className='absolute h-px w-3 bg-current' />
                      <span className='absolute h-3 w-px bg-current transition-transform duration-300 group-open:rotate-90 group-open:opacity-0' />
                    </span>
                  </summary>
                  <p
                    className={clsxm(
                      'max-w-2xl pb-7 pe-12 text-[16px] leading-7',
                      tone.body,
                    )}
                  >
                    {answer}
                  </p>
                </details>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
